import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { CarCard } from '../components/CarCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inventory() {
  const { t } = useLanguage();
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [condition, setCondition] = useState('all');
  const [make, setMake] = useState('all');
  const [makes, setMakes] = useState([]);

  const fetchCars = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (condition !== 'all') params.append('condition', condition);
      if (make !== 'all') params.append('make', make);
      params.append('limit', '50');

      const res = await axios.get(`${API}/cars?${params.toString()}`);
      setCars(res.data.cars || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch cars:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCars();
  }, [condition, make]);

  useEffect(() => {
    const timer = setTimeout(() => fetchCars(), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const res = await axios.get(`${API}/cars?limit=200`);
        const allCars = res.data.cars || [];
        const uniqueMakes = [...new Set(allCars.map((c) => c.make))].sort();
        setMakes(uniqueMakes);
      } catch (err) {
        console.error(err);
      }
    };
    fetchMakes();
  }, []);

  const clearFilters = () => {
    setSearch('');
    setCondition('all');
    setMake('all');
  };

  const hasFilters = search || condition !== 'all' || make !== 'all';

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-24" data-testid="inventory-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
            Partners4Cars
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white font-[Chivo]">
            {t('inventory.title')}
          </h1>
          <p className="text-base md:text-lg text-zinc-500 mt-4 max-w-2xl">
            {t('inventory.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div
          className="flex flex-col md:flex-row gap-4 mb-8 pb-8 border-b border-white/5 relative z-10"
          data-testid="inventory-filters"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inventory.search')}
              className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 h-11 rounded-sm focus:border-[#ED2227] focus:ring-[#ED2227]"
              data-testid="inventory-search-input"
            />
          </div>

          <Select value={make} onValueChange={setMake}>
            <SelectTrigger
              className="w-full md:w-48 bg-[#121212] border-zinc-800 text-white h-11 rounded-sm"
              data-testid="filter-make-select"
            >
              <SelectValue placeholder={t('inventory.filterMake')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="all" className="text-zinc-300">{t('inventory.filterMake')}</SelectItem>
              {makes.map((m) => (
                <SelectItem key={m} value={m} className="text-zinc-300">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={condition} onValueChange={setCondition}>
            <SelectTrigger
              className="w-full md:w-48 bg-[#121212] border-zinc-800 text-white h-11 rounded-sm"
              data-testid="filter-condition-select"
            >
              <SelectValue placeholder={t('inventory.filterCondition')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="all" className="text-zinc-300">{t('inventory.filterCondition')}</SelectItem>
              <SelectItem value="New" className="text-zinc-300">{t('inventory.filterNew')}</SelectItem>
              <SelectItem value="Used" className="text-zinc-300">{t('inventory.filterUsed')}</SelectItem>
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest gap-1"
              data-testid="clear-filters-button"
            >
              <X className="w-3 h-3" />
              {t('inventory.clearFilters')}
            </Button>
          )}
        </div>

        {/* Results Count */}
        <p className="text-xs font-mono tracking-widest text-zinc-600 mb-6 uppercase">
          {t('inventory.showing')} {cars.length} {t('inventory.of')} {total} {t('inventory.vehicles')}
        </p>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-[#121212] border border-zinc-800/50 rounded-md animate-pulse">
                <div className="aspect-[4/3] bg-zinc-800/50" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-zinc-800/50 rounded w-3/4" />
                  <div className="h-4 bg-zinc-800/50 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : cars.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-zinc-500 text-lg">{t('inventory.noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {cars.map((car) => (
              <CarCard key={car.id} car={car} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
